// New design system component: tinted status pill with optional icon

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Maps common backend statuses to a pill color + icon. Pages can extend or
// override this via the statusMap prop.
export const defaultStatusMap = {
  ACTIVE: { color: "success", icon: "check", label: "Active" },
  INACTIVE: { color: "secondary", icon: "remove", label: "Inactive" },
  COMPLETED: { color: "success", icon: "check", label: "Completed" },
  SUCCESS: { color: "success", icon: "check", label: "Success" },
  AUTHORIZED: { color: "success", icon: "check_circle", label: "Authorized" },
  SETTLED: { color: "success", icon: "check", label: "Settled" },
  SENT: { color: "success", icon: "check", label: "Sent" },
  PENDING: { color: "warning", icon: "schedule", label: "Pending" },
  PROCESSING: { color: "warning", icon: "sync", label: "Processing" },
  SETTLEMENT_INITIATED: { color: "warning", icon: "sync", label: "Settlement initiated" },
  FAILED: { color: "error", icon: "close", label: "Failed" },
  DECLINED: { color: "error", icon: "close", label: "Declined" },
  NOT_SENT: { color: "error", icon: "close", label: "Not sent" },
  REFUNDED: { color: "info", icon: "undo", label: "Refunded" },
  BLOCK: { color: "error", icon: "block", label: "Block" },
  BLOCKED: { color: "error", icon: "block", label: "Blocked" },
  APPROVAL_PENDING: { color: "warning", icon: "schedule", label: "Approval Pending" },
  IN_ACTIVE: { color: "secondary", icon: "remove", label: "In Active" },
};

function StatusPill({ status, label, statusMap, size }) {
  const key = typeof status === "string" ? status.toUpperCase().replace(/\s+/g, "_") : status;
  const entry = statusMap[key] || { color: "secondary", icon: null, label: status };
  const { color, icon } = entry;
  const displayLabel = label || entry.label || status;

  return (
    <MDBox
      component="span"
      display="inline-flex"
      alignItems="center"
      gap={0.5}
      sx={({ palette: { badgeColors }, functions: { pxToRem } }) => ({
        padding: size === "small" ? "2px 8px" : "3px 10px",
        borderRadius: pxToRem(999),
        fontSize: size === "small" ? "0.71875rem" : "0.75rem",
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        backgroundColor: (badgeColors[color] || badgeColors.secondary).background,
        color: (badgeColors[color] || badgeColors.secondary).text,
      })}
    >
      {icon && <Icon sx={{ fontSize: "0.8125rem !important" }}>{icon}</Icon>}
      {displayLabel}
    </MDBox>
  );
}

StatusPill.defaultProps = {
  label: null,
  statusMap: defaultStatusMap,
  size: "medium",
};

StatusPill.propTypes = {
  status: PropTypes.string.isRequired,
  label: PropTypes.node,
  statusMap: PropTypes.object,
  size: PropTypes.oneOf(["small", "medium"]),
};

export default StatusPill;
